import { getDataReaderClient } from "@multiweb/clients/util";
import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { IAttrValue, IRowData } from "@multiweb/types/contracts";
import { IDataSource, IDataSourceLoadInfo, IDBDataSourceParams } from "../../../Global/Base/DataSources/Interfaces";
import { Event } from "../../../Global/Base/Events";
import { CancellationToken } from "../../../Global/Base/Promises/CancellationTokenSource";
import { StringUtils } from "../../../Global/Base/Utils/StringUtils";
import { EOWException } from "../../../OW.Remoting/Basic/OWExceptions";
import { AttrValueReader } from "../../../OW.Remoting/Remoting/AttrValueReader";
import { OWDocumentDescription } from "../../../OW.Remoting/Remoting/DataTypes/OWDocumentDescription";
import { OWInt64 } from "../../../OW.Remoting/Remoting/DataTypes/OWInt64";
import { NullOWObject, OWObject } from "../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { OWManager } from "../../../OW.Remoting/Remoting/OWManager";
import { DBDataSource, IDBDataSourceLoadParams } from "./DBDataSource";
import { EditableDBDataSource } from "./EditableDBDataSource";

//@ts-ignore
const logger = LazyLoadingSectionLogger.create(LoggingSection.dataSource); // logger pro konkrétní oblast

export type ICommentaryDataSourceParams = Omit<IDBDataSourceParams, "dataLength" | "fetchCount">;

/**
* Trida reprezentujici databazovy zdroj dat pro konzumenta dat
*/
export class CommentaryFrameDataSource extends EditableDBDataSource {
    //#region Events
    /**
     * Vraci startindex pro cteni dat
     */
    public override readonly dataLoaded: Event<IDataSource, ICommentaryDataSourceLoadInfo> = new Event<IDataSource, ICommentaryDataSourceLoadInfo>();
    public readonly newCommentAdded: Event<IDataSource, ICommentaryDataSourceLoadInfo> = new Event<IDataSource, ICommentaryDataSourceLoadInfo>();
    //#endregion Events

    //#region Fields
    protected override get _internalRows(): readonly ICommentRowData[] {
        return super._internalRows as readonly ICommentRowData[];
    }
    protected override set _internalRows(value: readonly ICommentRowData[]) {
        super._internalRows = value;
    }
    protected _authorField: string;
    protected _dateField: string;
    protected _textField: string;
    protected _hierarchy: string;
    protected _rightToNew: boolean;
    protected _attachmentclass: string;
    protected _attachmentAttribute: string;
    protected _attachmentLinkLoAttr: string;
    protected _dsLoadInfo: ICommentaryDataSourceLoadInfo;

    public get rightToNew(): boolean {
        return this._rightToNew;
    }
    public set rightToNew(v: boolean) {
        this._rightToNew = v;
    }

    public static get datakey(): string {
        return new DBDataSource(null).dataKey;
    }

    public get authorField(): string {
        return this._authorField;
    }

    public set authorField(v: string) {
        this._authorField = v;
    }

    public get dateField(): string {
        return this._dateField;
    }

    public set dateField(v: string) {
        this._dateField = v;
    }

    public get textField(): string {
        return this._textField;
    }

    public set textField(v: string) {
        this._textField = v;
    }

    public get attachmentclass(): string {
        return this._attachmentclass;
    }

    public set attachmentclass(v: string) {
        this._attachmentclass = v;
    }

    public get attachmentAttribute(): string {
        return this._attachmentAttribute;
    }

    public set attachmentAttribute(v: string) {
        this._attachmentAttribute = v;
    }
    public get hierarchy(): string {
        return this._hierarchy;
    }
    public set hierarchy(v: string) {
        this._hierarchy = v;
    }
    public get attachmentLinkToAttr(): string {
        return this._attachmentLinkLoAttr;
    }
    public set attachmentLinkToAttr(v: string) {
        this._attachmentLinkLoAttr = v;
    }

    //#region Constructors
    constructor(params: ICommentaryDataSourceParams) {
        super({
            ...params,
            dataLength: -1,
            batchSize: -1,
        });

        this._internalRows = [];
    }
    //#endregion Constructors

    /**
     * Nahraje data od zacatku
     */
    public override loadNewData(keepSelectedRecords?: boolean, cancellationToken: CancellationToken = CancellationToken.none): Promise<IRowData[]> {
        this._condUpdaterBeforeLoadNewData();
        return super.loadNewData(keepSelectedRecords, cancellationToken);
    }

    protected _condUpdaterBeforeLoadNewData(): void {
        this.setAdditionalCondition("rootComments", this.hierarchy + "= @0", [null]);
    }

    //#region Methods
    public attachmentsEnabled(): boolean {
        return !!(this.attachmentclass && this.attachmentAttribute && this.attachmentLinkToAttr);
    }

    protected override _getIterator(startIndex: number, count?: number): IterableIterator<ICommentRowData> {
        return super._getIterator(startIndex, count) as IterableIterator<ICommentRowData>;
    }

    protected _cfRowDataHasParent(id: any): boolean {
        return !(id == 0 || id == null);
    }

    protected _rowToInternalRow(item: IRowData): ICommentRowData {
        const dataRow: ICommentRowData = {
            parentId: AttrValueReader.readRow(item, this.hierarchy),
            parentUserName: null,
            hasParent: this._cfRowDataHasParent(AttrValueReader.readRow(item, this.hierarchy)),
            data: item as ICommentaryRowData,
            attachmentClass: this._attachmentclass,
            attachmentAttribute: this._attachmentAttribute,
            attachments: [],
            children: [],
        };

        const authorAttrs = this.authorField.split(";");
        let author = "";
        if (authorAttrs.length > 1) {
            authorAttrs.forEach((authorAttr) => {
                if (!StringUtils.isNullOrWhiteSpace(authorAttr))
                    author += AttrValueReader.readRow(item, authorAttr) + " ";
            });
            author = author.trim();
        } else
            author = AttrValueReader.readRow(item, this.authorField);

        const text = AttrValueReader.readRow(item, this.textField);
        dataRow.data.id = AttrValueReader.readRow(item, this.dataKey);
        dataRow.data.author = author;
        dataRow.data.dateCreate = AttrValueReader.readRow(item, this.dateField);
        dataRow.data.text = text == null ? "" : text + "";
        dataRow.data.hierarchy = AttrValueReader.readRow(item, this.hierarchy);

        return dataRow;
    }

    //prida prilohu k zaznamu
    protected _fillAttachment(tempInternal: ICommentRowData[], aData: IRowData[]): void {
        tempInternal.forEach((cRow) => {
            aData.forEach((aRow) => {
                if (AttrValueReader.readRow(aRow, this.attachmentLinkToAttr) == AttrValueReader.readRow(cRow.data, this.dataKey)) {
                    cRow.attachments.push({ id: AttrValueReader.readRow(aRow, this.dataKey), attachmentLinkToAttr: AttrValueReader.readRow(aRow, this.attachmentLinkToAttr), attachmentAttribute: AttrValueReader.readRow(aRow, this.attachmentAttribute) });
                } else if (cRow.children.length > 0) {
                    this._fillAttachment(cRow.children, aData);
                }
            });
        });
    }

    //vyhleda prilohy
    protected async _addAttachment(tempInternal: ICommentRowData[], rowIds: any[]): Promise<void> {
        // je třeba načíst strom i pokud načítání příloh spadne
        try {
            await getDataReaderClient().getDataRowsWithParams(this._attachmentclass, [this.attachmentLinkToAttr, this.attachmentAttribute],
                "(ID <> 0) AND " + this._attachmentAttribute + " <> null AND " + this.attachmentLinkToAttr + " ∈ { " + rowIds.join(",") + "}")
                .then((aData: IRowData[]) => {
                    this._fillAttachment(tempInternal, aData);
                });
        } catch (e) {
            logger.error("Unable to load attachments", e);
        }
    }

    protected override async _processRows(params: IDBDataSourceLoadParams, cancellationToken: CancellationToken, rows: IRowData[], startIndex: number): Promise<IDataSourceLoadInfo> {
        const tempInternal = rows.map((item) => this._rowToInternalRow(item));
        this._internalRows = startIndex == 0 ? tempInternal : (this._internalRows || []).concat(tempInternal);

        if (tempInternal.length > 0) {
            const rowIds = rows.map((item) => {
                return AttrValueReader.readRow(item, this.dataKey);
            });
            this.removeAdditionalCondition("rootComments");
            this.setAdditionalCondition("rootComments", this.hierarchy + " ∈ {" + rowIds.join(",") + "}");
            //nactou se odpovedi pro nove pridane root komentare
            await getDataReaderClient().getDataRowsWithParams(this.className, this._attributes, this.condition, {
                orderBy: this._internalOrderBy,
                dataLength: this._dataLength,
                readVersioned: this._readVersioned,
                distinct: this.useDistinct,
            }).then(async (aData: any[]) => {
                //odpovedi se priradi k rootu
                tempInternal.forEach((cRow) => {
                    aData.forEach((aRow) => {
                        if (AttrValueReader.readRow(aRow, this.hierarchy) == AttrValueReader.readRow(cRow.data, this.dataKey)) {
                            cRow.children.push(this._rowToInternalRow(aRow));
                        }
                    });
                });

                //priradi se prilohy pro nove stazena data
                if (this._attachmentclass)
                    await this._addAttachment(tempInternal, rowIds.concat(aData.map((item) => {
                        return AttrValueReader.readRow(item, this.dataKey);
                    })));
            });

            this.removeAdditionalCondition("rootComments");
            this.setAdditionalCondition("rootComments", this.hierarchy + "= @0", [null]);
        }

        this._dsLoadInfo = this._afterProcessRow(params, cancellationToken) as ICommentaryDataSourceLoadInfo;
        this.loadingEnded.trigger(this, this._dsLoadInfo);

        return this._dsLoadInfo;
    }
    public async createNewComment(objectName: string, commentText: string, parentCommentId: string = "", masterLink?: string, actualObjectID?: string, masterAttr?: IAttrValue): Promise<{ comment: OWObject; error: EOWException }> {
        let error: EOWException;
        let comment: OWObject;

        try {
            comment = await OWManager.newObject(objectName, undefined, this._getRequestContext());

            if (masterLink) {
                if (masterAttr)
                    await comment.setValue(masterLink, masterAttr);
                else
                    await comment.setValue(masterLink, actualObjectID);
            }
            await comment.setValue(this.textField, commentText);
            if (!StringUtils.isNullOrWhiteSpace(parentCommentId))
                await comment.setValue(this.hierarchy, +parentCommentId);
            await comment.waitForUpdateInstance();
            await comment.applyUpdates();
        } catch (ex) {
            error = ex;
        }
        return { comment: comment, error: error };
    }

    public async saveAttachment(commentId: any, attachment: OWObject): Promise<{ attachment: OWObject; error: EOWException }> {
        let error: EOWException;
        try {
            await attachment.setValue(this.attachmentLinkToAttr, commentId);
            await attachment.applyUpdates();
        } catch (ex) {
            error = ex;
        }
        return { attachment, error };
    }

    protected async _getObjectDataKey(owObject: OWObject, attr: string): Promise<any> {
        const attrValue = await owObject.getValue(attr);
        return attrValue instanceof NullOWObject ? 0 : (attrValue instanceof OWObject ? attrValue.id : attrValue);
    }

    protected async _getAuthor(comment: OWObject): Promise<string> {
        let author = "";
        const authorAttrs = this.authorField.split(";");
        if (authorAttrs.length > 1) {
            for (let i = 0; i < authorAttrs.length; i++) {
                if (!StringUtils.isNullOrWhiteSpace(authorAttrs[i]))
                    author += (await comment.getValue(authorAttrs[i])) + " ";
            }
            author = author.slice(0, -1);
        } else
            author = AttrValueReader.read(await comment.getValue(this.authorField));
        return author;
    }

    public async commentToRowData(comment: OWObject): Promise<ICommentRowData> {
        const hierarchyDataKey = await this._getObjectDataKey(comment, this.hierarchy);
        return {
            parentId: hierarchyDataKey,
            parentUserName: null,
            hasParent: this._cfRowDataHasParent(hierarchyDataKey),
            data: {
                id: AttrValueReader.read(await comment.getValue(this.dataKey)),
                author: await this._getAuthor(comment),
                dateCreate: AttrValueReader.read(await comment.getValue(this.dateField)),
                text: AttrValueReader.read(await comment.getValue(this.textField)),
                hierarchy: hierarchyDataKey,
            },
            attachmentClass: this._attachmentclass,
            attachmentAttribute: this._attachmentAttribute,
            attachments: [],
            children: [],
        };
    }

    public addNewCommentToDataRows(comment: ICommentRowData): void {
        this._internalRows = [comment, ...this._internalRows];

        if (this._dsLoadInfo == undefined) {
            const params: IDBDataSourceLoadParams = { startIndexForEvent: 0 };
            this._allDataRead = true;
            this._dsLoadInfo = this._afterProcessRow(params, CancellationToken.none) as ICommentaryDataSourceLoadInfo;
        } else {
            this.newCommentAdded.trigger(this, this._dsLoadInfo);
        }
    }

    public async appendAttachment(actualObject: OWObject): Promise<ICommentaryFrameRowDataAttachment> {
        const id: OWInt64 = await actualObject.getValue(this.dataKey) as OWInt64;
        const attachmentLinkToAttr: OWObject = await actualObject.getValue(this.attachmentLinkToAttr) as OWObject;
        const attachmentAttribute: OWDocumentDescription = await actualObject.getValue(this.attachmentAttribute) as OWDocumentDescription;
        return {
            id: id,
            attachmentLinkToAttr: attachmentLinkToAttr.id,
            attachmentAttribute: attachmentAttribute.value,
        };
    }
}

export interface ICommentRowData extends IRowData {
    parentId: string;
    /**
     * obsahuje @uživatelské jméno u komentářů třetí a nižší úrovně
     */
    parentUserName: string;
    hasParent: boolean;
    data: ICommentaryRowData;
    attachmentClass: string;
    attachmentAttribute: string;
    attachments: ICommentaryFrameRowDataAttachment[];
    children: ICommentRowData[];
    [nope: string]: any;
}

export interface ICommentaryRowData extends IRowData {
    id: string;
    author: string;
    dateCreate: string;
    text: string;
    hierarchy: string;
}

export interface ICommentaryFrameRowDataAttachment extends IRowData {
    id: OWInt64;
    attachmentLinkToAttr: string;
    attachmentAttribute: string;
}

export class ICommentaryDataSourceLoadInfo extends IDataSourceLoadInfo {
    public override dataGenerator: () => Iterable<ICommentRowData>;
}
